import type React from 'react';
import {
  useTheme,
  resolveLessonThemeColor,
  type LessonThemeColor,
} from '@course-authoring/shared-types';
import { useTranslation } from '@course-authoring/i18n';
import { useIsMobileViewport } from './hooks/useIsMobileViewport';

export interface LessonTitleHeaderProps {
  title: string;
  themeColor?: LessonThemeColor;
  /** 1-based lesson index within a course. Omit for standalone lesson view. */
  lessonIndex?: number;
  /** Total number of lessons in the course. Omit for standalone lesson view. */
  totalLessons?: number;
  /**
   * When true, content (e.g. the course-completion banner) renders directly above
   * this header, so the large top breathing room is dropped — the preceding
   * element owns the gap. Defaults to false (header is first; full top padding).
   */
  hasContentAbove?: boolean;
}

export function LessonTitleHeader({
  title,
  themeColor,
  lessonIndex,
  totalLessons,
  hasContentAbove = false,
}: LessonTitleHeaderProps): React.ReactElement {
  const { tokens } = useTheme();
  const { t } = useTranslation();
  const isMobile = useIsMobileViewport(tokens.layout.mobileBreakpoint);
  const isSmallMobile = useIsMobileViewport(tokens.layout.smallMobileBreakpoint);

  const lineColor = resolveLessonThemeColor(themeColor, tokens.lessonColors);
  const showLessonPosition =
    typeof lessonIndex === 'number' &&
    typeof totalLessons === 'number' &&
    totalLessons > 1;

  const styles = createStyles(tokens, lineColor, isMobile, isSmallMobile, hasContentAbove);

  return (
    <div style={styles.container}>
      <div style={styles.grid}>
        <div style={styles.content}>
          <div style={styles.titleGroup}>
            {showLessonPosition && (
              <p style={styles.lessonPosition}>
                {t('lesson_title.lesson_of', { lessonIndex, totalLessons })}
              </p>
            )}
            <h1 style={styles.title}>{title || t('lesson_title.untitled')}</h1>
          </div>
          <div style={styles.themeColorLine} />
        </div>
      </div>
    </div>
  );
}

function createStyles(
  tokens: ReturnType<typeof useTheme>['tokens'],
  lineColor: string,
  isMobile: boolean,
  isSmallMobile: boolean,
  hasContentAbove: boolean,
) {
  const sectionPaddingX = isMobile
    ? tokens.layout.sectionPaddingX.mobile
    : '0px';
  // NA-205: tighten the title's vertical breathing room on narrow phones (≤576px).
  const verticalPadding = isSmallMobile ? '75px' : '100px';

  return {
    container: {
      width: '100%',
      backgroundColor: tokens.backgrounds.lessonSectionDefault,
      // Drop the top breathing room when a banner sits above (it owns the 50px gap).
      padding: `${hasContentAbove ? '0px' : verticalPadding} ${sectionPaddingX} ${verticalPadding}`,
      boxSizing: 'border-box' as const,
    },
    // 10-column grid: 1 left gutter | 6 content | 3 right gutter
    grid: {
      display: isMobile ? 'flex' : 'grid',
      gridTemplateColumns: isMobile ? undefined : '1fr 6fr 3fr',
      width: '100%',
      maxWidth: tokens.layout.contentMaxWidth,
      margin: '0 auto',
      boxSizing: 'border-box' as const,
    } as React.CSSProperties,
    content: {
      gridColumn: isMobile ? undefined : '2 / 3',
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'flex-start',
      // Figma 3987:715 — 38px between the title group and the theme-color line;
      // NA-205 tightens this to 20px on narrow phones (≤576px).
      gap: isSmallMobile ? '20px' : '38px',
    },
    // Figma 3987:716 — eyebrow ("Lesson X of Y") sits just above the title. The title's
    // tight 65px line-height lets its ascenders overflow upward, so a small gap keeps
    // the eyebrow clear of the title glyphs instead of overlapping them.
    titleGroup: {
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'flex-start',
      gap: '12px',
    },
    lessonPosition: {
      margin: 0,
      fontSize: '18px',
      fontWeight: 400,
      lineHeight: '100%',
      color: tokens.text.primary,
      fontFamily: tokens.typography.fontFamily,
    },
    title: {
      margin: 0,
      fontSize: isMobile ? '38px' : '60px',
      fontWeight: 700,
      // Line-height tracks the font-size drop: 65px suits the 60px desktop title,
      // but on the 38px mobile title (≤768px) a single 48px reads better than the
      // old 65px (too loose mid-range) / 42px (too tight ≤576px) split. (NA-205)
      lineHeight: isMobile ? '48px' : '65px',
      color: tokens.text.primary,
      fontFamily: tokens.typography.fontFamily,
      wordBreak: 'break-word' as const,
    },
    themeColorLine: {
      height: '9px',
      width: '262px',
      backgroundColor: lineColor,
    },
  };
}

LessonTitleHeader.displayName = 'LessonTitleHeader';
